import React, { useState, useEffect, useCallback } from "react";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Card, Container, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { v4 } from "uuid";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import ContentWrapper from "../../../components/content-wrapper";
import { ErrorField } from "../../shared/widgets/TranslationComponent/styles";
import { tickerHookFormValidation } from "../../shared/crud/form-validation";
import { InputToggle } from "../../bundle/components/InputToggle";
import {
  GlobalActionsConfigEntity,
  GlobalActionsConfigInput,
  GlobalActionsConfigSchema,
  createEmptyGlobalActionsConfig,
} from "./domain/globalActionConfig.entity";
import { GlobalActionsConfigGrpcRepository } from "./infrastructure/repositories/GlobalActionsConfigGrpcRepository";
import { useGlobalActionsConfigMutations } from "./application/hooks/useGlobalActionsConfigMutations";
import { AddActionModal } from "../shared/components/AddActionModal";
import { AddCriteriaModal } from "../shared/components/AddCriteriaModal";
import { CriteriaTable } from "../shared/components/CriteriaTable";
import { ActionsTable } from "../shared/components/ActionsTable";
import { isKnownActionKey } from "../shared/domain/globalAction.types";
import { MatchMode } from "../shared/domain/orchestrationCriteria.entity";
import { useMatchModeLabels } from "../orchestrator.messages";
import { WitLabel } from "../../shared/widgets/common";
import { messages } from "./globalAction.messages";
import { useOccupations, useActionsManager, useCriteriaManager } from "../shared/hooks";

const globalActionsRepository = new GlobalActionsConfigGrpcRepository();

function GlobalActionEdit() {
  const intl = useIntl();
  const matchModeLabels = useMatchModeLabels();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const propertyInfo = useProperty();
  const { createConfig, updateConfig } = useGlobalActionsConfigMutations(
    propertyInfo.ticker,
    globalActionsRepository
  );
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const form = useForm<GlobalActionsConfigInput, unknown, GlobalActionsConfigEntity>({
    resolver: zodResolver(GlobalActionsConfigSchema),
    defaultValues: createEmptyGlobalActionsConfig(v4()),
  });

  const { occupations, loading: loadingOccupations } = useOccupations(propertyInfo.ticker);
  const actionsManager = useActionsManager(form, "actions");
  const criteriaManager = useCriteriaManager(form);

  useEffect(() => {
    if (!isNew && id) {
      const loadConfig = async () => {
        try {
          const config = await globalActionsRepository.findById(propertyInfo.ticker, id);
          if (config) {
            form.reset(config);
          }
        } catch (error) {
          console.error("Error loading GlobalActionsConfig:", error);
        } finally {
          setLoading(false);
        }
      };
      loadConfig();
    }
  }, [isNew, id, propertyInfo.ticker, form]);

  const handleGoBack = useCallback(() => {
    history.push(`/${propertyInfo.ticker}/orchestrator/actions`);
  }, [history, propertyInfo.ticker]);

  const handleSubmit = useCallback(
    async (data: GlobalActionsConfigEntity) => {
      try {
        setSaving(true);
        if (isNew) {
          await createConfig(data);
        } else {
          await updateConfig(data);
        }
        handleGoBack();
      } catch (error) {
        console.error("Error saving GlobalActionsConfig:", error);
      } finally {
        setSaving(false);
      }
    },
    [isNew, createConfig, updateConfig, handleGoBack]
  );

  if (loading || loadingOccupations) {
    return (
      <ContentWrapper>
        <SectionHeading>
          <FormattedMessage {...messages.title} />
        </SectionHeading>
        <Container className="p-4" fluid={true}>
          <Card className="p-3">
            <FormattedMessage {...messages.loading} />
          </Card>
        </Container>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Container className="p-4" fluid={true}>
        <Card className="p-3">
          <FormProvider {...form}>
            <Form>
              {/* Active Toggle */}
              <FormGroup>
                <FormLabel>
                  <FormattedMessage {...messages.active} />
                </FormLabel>
                <Controller
                  name="active"
                  control={form.control}
                  render={({ field }) => (
                    <InputToggle
                      className="witadmin-bs3"
                      disabled={saving}
                      {...field}
                      value={field.value ?? true}
                    />
                  )}
                />
              </FormGroup>

              {/* Ticker */}
              <FormGroup>
                <FormLabel htmlFor="tickerInput">
                  <FormattedMessage {...messages.ticker} />
                </FormLabel>
                <FormControl
                  id="tickerInput"
                  type="text"
                  className="!w-[200px]"
                  disabled={!isNew || saving}
                  {...form.register("ticker", tickerHookFormValidation(intl))}
                />
                {form.formState.errors["ticker"] && (
                  <ErrorField>{form.formState.errors["ticker"].message}</ErrorField>
                )}
              </FormGroup>

              {/* Match Mode */}
              <FormGroup>
                <div className="hidden">
                  <FormLabel>
                    <FormattedMessage {...messages.matchMode} />
                  </FormLabel>
                  <Controller
                    name="criteria.matchMode"
                    control={form.control}
                    render={({ field }) => (
                      <Form.Control
                        as="select"
                        className="!w-[300px]"
                        value={field.value || MatchMode.ALL}
                        onChange={e => field.onChange(e.target.value as MatchMode)}>
                        {Object.values(MatchMode)
                          .filter(m => m !== MatchMode.MATCH_MODE_UNSPECIFIED)
                          .map(mode => (
                            <option key={mode} value={mode}>
                              {matchModeLabels[mode]}
                            </option>
                          ))}
                      </Form.Control>
                    )}
                  />
                </div>
              </FormGroup>

              {/* Conditions */}
              <FormGroup>
                <WitLabel>
                  <FormLabel>
                    <FormattedMessage {...messages.conditions} />
                  </FormLabel>
                </WitLabel>

                <div className="mb-3">
                  <CriteriaTable
                    conditions={criteriaManager.conditions}
                    onEdit={criteriaManager.handleEdit}
                    onDelete={criteriaManager.handleDelete}
                  />
                </div>

                <div>
                  <Button
                    variant="primary"
                    onClick={criteriaManager.handleOpenAdd}
                    disabled={saving}>
                    <FormattedMessage {...messages.add} />
                  </Button>
                </div>
              </FormGroup>

              {/* Global Actions */}
              <FormGroup>
                <WitLabel>
                  <FormLabel>
                    <FormattedMessage {...messages.actionsToExecute} />
                  </FormLabel>
                </WitLabel>

                <div className="mb-3">
                  <ActionsTable
                    actions={actionsManager.actions}
                    onEdit={actionsManager.handleEdit}
                    onDelete={actionsManager.handleDelete}
                  />
                </div>

                <div>
                  <Button
                    variant="primary"
                    onClick={actionsManager.handleOpenAdd}
                    disabled={saving}>
                    <FormattedMessage {...messages.addAction} />
                  </Button>
                </div>
              </FormGroup>

              {/* Form Actions */}
              <div className="flex gap-4 mt-4 pt-4 border-top">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving}
                  onClick={form.handleSubmit(handleSubmit)}>
                  {!saving && <FormattedMessage {...messages.save} />}
                  {saving && (
                    <>
                      <FormattedMessage {...messages.saving} />{" "}
                      <FontAwesomeIcon icon={faCircleNotch} spin />
                    </>
                  )}
                </Button>
                <Button variant="secondary" onClick={handleGoBack} disabled={saving}>
                  <FormattedMessage {...messages.back} />
                </Button>
              </div>
            </Form>
          </FormProvider>
        </Card>
      </Container>

      <AddCriteriaModal
        isOpen={criteriaManager.modalOpen}
        onClose={criteriaManager.handleClose}
        onSave={criteriaManager.handleSave}
        editingCondition={criteriaManager.editingCondition}
        editingIndex={criteriaManager.editingIndex ?? undefined}
        existingConditionTypes={criteriaManager.existingConditionTypes}
        occupations={occupations}
      />

      <AddActionModal
        isOpen={actionsManager.modalOpen}
        onClose={actionsManager.handleClose}
        onSave={actionsManager.handleSave}
        editingKey={
          actionsManager.editingKey && isKnownActionKey(actionsManager.editingKey)
            ? actionsManager.editingKey
            : undefined
        }
        editingValue={actionsManager.editingValue}
        existingKeys={actionsManager.existingKeys}
      />
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(GlobalActionEdit);
